/**
 * "Passkeys" on My sign-in (decision 804, docs/specs/cloud/sign-in-security.md §5): the person's passkeys, "Add a
 * passkey" and "Remove". Someone with two-step sign-in on enters a code first, as for removing an authenticator app,
 * so a one-step session cannot add a way in.
 */
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { Button } from '../components/Button.tsx';
import { ErrorState, Loading } from '../components/States.tsx';
import { addPasskey, listPasskeys, removePasskey, type Passkey } from './api.ts';

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));
const day = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });

export function PasskeysPanel({
  sb,
  needsCode,
  onNeedCode,
}: {
  readonly sb: SupabaseClient;
  /** Two-step sign-in is on and this session has not entered a code. */
  readonly needsCode: boolean;
  /** Ask for the code (the two-step panel above shows the code box). */
  readonly onNeedCode: () => void;
}): ReactElement {
  const [keys, setKeys] = useState<Passkey[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ readonly ok: boolean; readonly text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<Passkey | null>(null);

  const reload = useCallback(() => {
    setError(null);
    listPasskeys(sb)
      .then(setKeys)
      .catch((e: unknown) => setError(message(e)));
  }, [sb]);
  useEffect(reload, [reload]);

  async function add(): Promise<void> {
    if (needsCode) return onNeedCode();
    setNotice(null);
    setBusy(true);
    try {
      await addPasskey(sb);
      setNotice({ ok: true, text: 'Passkey added. Next time, press "Sign in with a passkey".' });
      reload();
    } catch (e: unknown) {
      setNotice({ ok: false, text: message(e) });
    } finally {
      setBusy(false);
    }
  }

  async function remove(p: Passkey): Promise<void> {
    setConfirm(null);
    setBusy(true);
    try {
      await removePasskey(sb, p.id);
      setNotice({ ok: true, text: `${p.name} removed.` });
      reload();
    } catch (e: unknown) {
      setNotice({ ok: false, text: message(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel stack" aria-labelledby="passkeys-title">
      <h2 id="passkeys-title" className="heading">
        Passkeys
      </h2>
      <p className="body">
        A passkey signs you in with this computer’s fingerprint, face or PIN, or with your phone, instead of
        an email link. It is the first step only: if two-step sign-in is on, you still enter the code.
      </p>
      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      {error && <ErrorState message={error} onRetry={reload} />}
      {!error && !keys && <Loading what="your passkeys" />}
      {keys && (
        <>
          <p className="body">
            {keys.length === 0 ? 'None yet.' : keys.length === 1 ? '1 passkey.' : `${keys.length} passkeys.`}
          </p>
          {keys.length > 0 && (
            <ul className="stack">
              {keys.map((k) => (
                <li key={k.id} className="row">
                  <span className="body">
                    {k.name}{' '}
                    <span className="caption muted">
                      · added {day(k.createdAt)}
                      {k.lastUsedAt ? ` · last used ${day(k.lastUsedAt)}` : ''}
                    </span>
                  </span>
                  <Button
                    variant="quiet"
                    aria-label={`Remove ${k.name}`}
                    disabled={busy}
                    onClick={() => (needsCode ? onNeedCode() : setConfirm(k))}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}
          {confirm && (
            <div className="panel confirm" role="dialog" aria-labelledby="remove-passkey-title">
              <p id="remove-passkey-title" className="body">
                Remove {confirm.name}? It stops signing you in straight away. Delete it from the device or
                password manager that holds it as well.
              </p>
              <div className="row">
                <Button className="btn--danger" onClick={() => void remove(confirm)}>
                  Remove passkey
                </Button>
                <Button variant="quiet" onClick={() => setConfirm(null)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
          <div className="row">
            <Button busy={busy} onClick={() => void add()}>
              {needsCode ? 'Enter a code to add a passkey' : 'Add a passkey'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
